import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useV3Theme } from "../../../hooks/use-v3-theme";
import { useChatRail } from "../../chat/ChatRailContext";
import { useConnect } from "../../../hooks/use-connect";
import { useAuth } from "../../../../lib/hooks/use-auth";
import { useWorkspaceInfo } from "../../../../lib/hooks/use-kuvio";
import { mcpClient } from "../../../../lib/mcp-client";
import { showToast } from "../../primitives";
import type { Command, CommandContext } from "../types";

/**
 * Action commands (S3.12) — workspace-surface routes plus system utilities that
 * complement the UI buttons without being gated behind menu-hunting: theme,
 * right rail, refresh, copy workspace info, sign out, and a switch-workspace
 * command per accessible workspace (read from cached auth state — no network).
 */

const SURFACES: { path: string; label: string; icon: string }[] = [
  { path: "/workforce", label: "Go to Workforce", icon: "users" },
  { path: "/tables", label: "Go to Tables", icon: "table" },
  { path: "/library", label: "Go to Library", icon: "folder" },
  { path: "/connections", label: "Go to Connections", icon: "plug" },
  { path: "/my-tasks", label: "Go to My tasks", icon: "checklist" },
  { path: "/activity", label: "Go to Activity", icon: "activity" },
  { path: "/settings/workspace", label: "Go to Settings", icon: "settings" },
];

export function useActionCommands(ctx: CommandContext): Command[] {
  const { navigate } = ctx;
  const { toggle: toggleTheme } = useV3Theme();
  const { toggleExpanded: toggleChat, openWith, scope: chatScope } = useChatRail();
  const { user, currentWorkspaceId, switchWorkspace, logout } = useAuth();
  const wsInfo = useWorkspaceInfo();
  const qc = useQueryClient();
  const { hasRecentSession } = useConnect();

  const wsName = wsInfo.data?.workspace.name;

  return useMemo(() => {
    const cmds: Command[] = [];
    const workspaces = user?.workspaces ?? [];

    for (const s of SURFACES) {
      cmds.push({
        id: `action:goto:${s.path}`,
        label: s.label,
        icon: s.icon,
        kind: "action",
        subtitle: "Workspace",
        keywords: ["go", "open", "navigate"],
        execute: () => navigate(s.path),
      });
    }

    cmds.push({
      id: "action:theme",
      label: "Toggle dark mode",
      icon: "moon",
      kind: "action",
      keywords: ["theme", "light", "dark", "appearance"],
      execute: () => toggleTheme(),
    });
    cmds.push({
      id: "action:chat",
      label: "Toggle chat panel",
      icon: "layout-sidebar-right",
      kind: "action",
      keywords: ["panel", "sidebar", "rail", "chat", "kuvio"],
      execute: () => toggleChat(),
    });

    // Sprint 22 — admin chat (cockpit) commands. The chat lives in the right
    // panel on every route; these open / focus / reset it. Scope is route-driven
    // (useChatScope), so "New chat" inherits whatever the current page declared.
    const scopeLabel = chatScope.label ?? (chatScope.kind === "workspace" ? "this workspace" : `this ${chatScope.kind}`);
    cmds.push({
      id: "action:ask-kuvio",
      label: "Ask Kuvio…",
      icon: "message-circle",
      kind: "action",
      subtitle: "Chat",
      keywords: ["chat", "ask", "ai", "assistant", "kuvio", "cockpit"],
      execute: () => openWith({}),
    });
    cmds.push({
      id: "action:new-chat",
      label: `New chat about ${scopeLabel}`,
      icon: "message-plus",
      kind: "action",
      subtitle: "Chat",
      keywords: ["chat", "new", "fresh", "start", "ask"],
      execute: () => openWith({ newChat: true }),
    });
    cmds.push({
      id: "action:continue-chat",
      label: "Continue last chat",
      icon: "message",
      kind: "action",
      subtitle: "Chat",
      keywords: ["chat", "continue", "resume", "history", "recent"],
      execute: () => openWith({}),
    });
    cmds.push({
      id: "action:chat-history",
      label: "Show chat history",
      icon: "history",
      kind: "action",
      subtitle: "Chat",
      keywords: ["chat", "history", "sessions", "past", "previous"],
      execute: () => openWith({}),
    });
    cmds.push({
      id: "action:ask-passage",
      label: "Ask about this passage",
      icon: "quote",
      kind: "action",
      subtitle: "Chat",
      keywords: ["chat", "selection", "passage", "ask", "quote", "explain"],
      execute: () => {
        const sel = typeof window !== "undefined" ? window.getSelection()?.toString().trim() : "";
        if (sel) openWith({ draft: `About this passage:\n\n"${sel}"\n\n` });
        else openWith({});
      },
    });
    cmds.push({
      id: "action:refresh",
      label: "Refresh workspace data",
      icon: "refresh",
      kind: "action",
      keywords: ["reload", "refetch", "sync"],
      execute: () => {
        void qc.invalidateQueries();
        showToast("Refreshing workspace data…");
      },
    });

    // Sprint 21 — continuous-improvement loop. Find recurring patterns across the
    // current agent's accumulated learnings (compose/apply-learnings are
    // per-task, surfaced as buttons on the task's Continuous Improvement
    // section). Shown only on an agent cockpit, where there's an agent to scope to.
    if (ctx.currentNodeType === "agent" && ctx.currentNodeId) {
      const agentId = ctx.currentNodeId;
      cmds.push({
        id: "action:find-patterns",
        label: `Find patterns in ${ctx.currentNodeName ?? "this agent"}`,
        icon: "repeat",
        kind: "action",
        subtitle: "Continuous improvement",
        keywords: ["patterns", "learnings", "retrospective", "improve", "themes"],
        execute: () => {
          showToast("Finding patterns…");
          mcpClient
            .toolsCall("find_patterns", { agent_id: agentId })
            .then((r) => {
              if (r.isError) throw new Error(r.content?.[0]?.text ?? "failed");
              void qc.invalidateQueries({ queryKey: ["child-nodes"] });
              showToast(r.content?.[0]?.text ?? "Pattern detection complete");
            })
            .catch(() => showToast("Couldn't find patterns", "error", true));
        },
      });
    }

    // Sprint 16d — open the AI setup wizard for a new wrapped API.
    cmds.push({
      id: "action:connect-new-api",
      label: "Connect new API",
      icon: "plug",
      kind: "action",
      subtitle: "Connections",
      keywords: ["api", "connect", "wrap", "integration", "openapi", "zendesk", "hubspot", "stripe", "wizard"],
      execute: () => navigate("/connections?wizard=1"),
    });

    // Sprint 14f (Layer 5): manual lever to re-synthesise the MCP tool list when an
    // AI host's cache goes stale after an entity-type change. Shown only when the
    // user actually has a recent MCP session (S14F.13) — no value otherwise.
    if (hasRecentSession) {
      cmds.push({
        id: "action:refresh-tools",
        label: "Refresh tools",
        icon: "tools",
        kind: "action",
        subtitle: "Sync MCP tool list",
        keywords: ["refresh", "tools", "tool list", "cache", "stale", "mcp", "sync", "entity"],
        execute: () => {
          mcpClient
            .refreshTools()
            .then((r) => {
              const total = r.static_tool_count + r.entity_tool_count;
              const n = r.entity_types.length;
              showToast(
                `Tool list refreshed — ${total} tools (${r.entity_tool_count} from ${n} entity type${n === 1 ? "" : "s"})`,
              );
            })
            .catch(() => showToast("Couldn't refresh tools", "error", true));
        },
      });
    }
    cmds.push({
      id: "action:copy-workspace",
      label: "Copy workspace info",
      icon: "clipboard",
      kind: "action",
      keywords: ["copy", "id", "info"],
      execute: () => {
        const text = `${wsName ?? "Workspace"} (${currentWorkspaceId ?? ""})`;
        navigator.clipboard
          .writeText(text)
          .then(() => showToast("Workspace info copied"))
          .catch(() => showToast("Couldn't copy to clipboard", "error", true));
      },
    });
    cmds.push({
      id: "action:signout",
      label: "Sign out",
      icon: "logout",
      kind: "action",
      keywords: ["log out", "logout", "exit"],
      execute: () => {
        void logout();
      },
    });

    for (const ws of workspaces) {
      if (ws.id === currentWorkspaceId) continue;
      cmds.push({
        id: `action:switch-workspace:${ws.id}`,
        label: `Switch to ${ws.name}`,
        icon: "arrows-left-right",
        kind: "action",
        subtitle: "Switch workspace",
        keywords: ["switch", "workspace", ws.slug],
        execute: () => {
          void switchWorkspace(ws.id);
        },
      });
    }

    return cmds;
  }, [navigate, toggleTheme, toggleChat, openWith, chatScope.kind, chatScope.label, qc, wsName, currentWorkspaceId, logout, switchWorkspace, user, hasRecentSession, ctx.currentNodeType, ctx.currentNodeId, ctx.currentNodeName]);
}
